import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, MapPin, Instagram, ChevronRight, Check, Clock, Users, Target, ShoppingBag, ArrowLeft, Mail } from "lucide-react";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Coach photos - All photos for carousel
const ALL_COACH_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/sf1snrmv_IMG_4964.jpeg",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/u93pi38e_Formula%20x%20MAP-24.JPG",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/1k00fxf3_Formula%20x%20MAP-15.JPG",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/jodnjceq_Formula%20x%20MAP-18.JPG",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/n5out3q2_Formula%20x%20MAP-57.JPG",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/7txt61ck_0L6A8943.JPG",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/suou9bcc_564AFD22-CFB5-44D5-831D-4D1BAF919C04.JPG",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/0kd469n6_0L6A5390.JPG",
  "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/ej22n9aj_Screen%20Shot%202018-11-24%20at%208.33.26%20PM.png"
];

const COACH_IMAGES = {
  hero: ALL_COACH_PHOTOS[0],
  about: "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/w38clhf3_564AFD22-CFB5-44D5-831D-4D1BAF919C04.JPG",
  services: ALL_COACH_PHOTOS[2],
  contact: ALL_COACH_PHOTOS[3]
};

const CALENDLY_LINK = "https://calendly.com/mahkibackstrom/baseball?month=2026-01";

// Photo Carousel Component
const PhotoCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ALL_COACH_PHOTOS.length);
    }, 4000); // Change every 4 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm bg-[#0A0A0A]">
      {ALL_COACH_PHOTOS.map((photo, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={photo}
            alt={`Coach photo ${index + 1}`}
            className="w-full h-full object-contain"
          />
        </div>
      ))}
      {/* Dots indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {ALL_COACH_PHOTOS.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex ? 'bg-[#CCFF00] w-6' : 'bg-white/30'
            }`}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ scrolled }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isShopPage = location.pathname === '/shop';

  const navLinks = [
    { href: isShopPage ? "/#about" : "#about", label: "About" },
    { href: isShopPage ? "/#pricing" : "#pricing", label: "Pricing" },
    { href: isShopPage ? "/#success" : "#success", label: "Success" },
    { href: isShopPage ? "/#testimonials" : "#testimonials", label: "Reviews" },
    { href: "/shop", label: "Shop", isRoute: true },
    { href: isShopPage ? "/#book" : "#book", label: "Book Now" }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'header-blur py-4' : 'py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" className="font-anton text-xl md:text-2xl tracking-wider" data-testid="nav-logo">
          THE FORMULA
        </Link>
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link key={link.href} to={link.href} className="nav-link" data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} className="nav-link" data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                {link.label}
              </a>
            )
          ))}
          <a href={CALENDLY_LINK} target="_blank" rel="noopener noreferrer">
            <Button className="btn-primary text-sm" data-testid="nav-cta">
              Schedule Now
            </Button>
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? 'open' : ''}`}>
        <div className="p-6 flex flex-col gap-6 pt-20">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link 
                key={link.href} 
                to={link.href} 
                className="text-white text-lg font-medium"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ) : (
              <a 
                key={link.href} 
                href={link.href} 
                className="text-white text-lg font-medium"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            )
          ))}
          <a href={CALENDLY_LINK} target="_blank" rel="noopener noreferrer" className="mt-4">
            <Button className="btn-primary w-full">Schedule Now</Button>
          </a>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center py-32 px-6 md:px-12 relative noise-bg" id="hero">
      <div className="max-w-7xl mx-auto w-full relative z-10">
        <div className="hero-grid items-center">
          {/* Left - Typography */}
          <div className="flex flex-col gap-6">
            <div>
              <h1 className="font-anton text-5xl sm:text-6xl lg:text-8xl leading-none tracking-wide">
                <span className="block">THE</span>
                <span className="block text-[#CCFF00]">FORMULA</span>
                <span className="block">BASEBALL</span>
              </h1>
            </div>
            <p className="text-[#A1A1AA] text-base md:text-lg max-w-md font-manrope">
              Elite private hitting instruction in Los Angeles. Transform your swing. Elevate your game.
            </p>
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <MapPin size={18} className="text-[#CCFF00]" />
              <span className="text-sm">Los Angeles, California</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <a href="#book">
                <Button className="btn-primary pulse-lime" data-testid="hero-book-btn">
                  Book Your Session
                </Button>
              </a>
              <a href="#pricing">
                <Button className="btn-secondary" data-testid="hero-pricing-btn">
                  View Pricing
                </Button>
              </a>
            </div>
          </div>

          {/* Right - Photo Carousel */}
          <div className="mt-8 lg:mt-0 h-[400px] md:h-[500px] lg:h-[600px]">
            <PhotoCarousel />
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const stats = [
    { number: "8", label: "Years Pro" },
    { number: "250+", label: "Hitters Trained" },
    { number: "100%", label: "Dedication" }
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 section-darker relative" id="about">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden rounded-sm">
              <img 
                src={COACH_IMAGES.about}
                alt="Coach Mahki"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#CCFF00] text-black p-6 rounded-sm hidden md:block">
              <span className="font-anton text-3xl">8</span>
              <span className="block text-sm font-bold uppercase tracking-wider">Years Pro</span>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="text-[#CCFF00] font-manrope font-semibold uppercase tracking-widest text-sm">
              About The Coach
            </span>
            <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
              MAHKI BACKSTROM
            </h2>
            <div className="space-y-4 text-[#A1A1AA] font-manrope">
              <p>
                With 8 years of professional baseball experience and player development, 
                I've dedicated my career to helping hitters unlock their full potential at the plate.
              </p>
              <p>
                The Formula isn't just about mechanics—it's about understanding the science of hitting, 
                building mental toughness, and developing a swing that works for YOUR body.
              </p>
              <p>
                We also offer vision training through the Applied Vision application, helping players 
                sharpen their pitch recognition and reaction time for a complete competitive edge.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-white/10">
              {stats.map((stat, i) => (
                <div key={i}>
                  <div className="stat-number">{stat.number}</div>
                  <div className="text-[#A1A1AA] text-sm mt-1 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onSelectPackage }) => {
  const singlePackages = [
    {
      id: "half_hour",
      name: "30 Min Session",
      price: 45,
      duration: "30 minutes",
      description: "Quick focused session",
      features: ["Video analysis", "Swing corrections", "Drill work"],
      icon: Clock
    },
    {
      id: "single_hour",
      name: "1 Hour Private",
      price: 90,
      duration: "60 minutes",
      description: "Full training session",
      features: ["Complete swing analysis", "Personalized drills", "Mental approach", "Take-home plan"],
      icon: Target,
      popular: true
    },
    {
      id: "group_hour",
      name: "Group Session",
      price: 140,
      duration: "60 minutes",
      description: "Training for 2 players",
      features: ["Partner drills", "Competition reps", "Shared feedback", "Great for teammates"],
      icon: Users
    }
  ];

  const multiPackages = [
    {
      id: "four_pack",
      name: "4-Pack",
      price: 300,
      duration: "4 x 60 min",
      description: "Save $60",
      features: ["4 private sessions", "Flexible scheduling", "Progress tracking", "Best for beginners"],
      icon: Target,
      savings: 60
    },
    {
      id: "six_pack",
      name: "6-Pack",
      price: 420,
      duration: "6 x 60 min",
      description: "Save $120",
      features: ["6 private sessions", "Priority booking", "Video library access", "Best value"],
      icon: Target,
      popular: true,
      savings: 120
    }
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12" id="pricing">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-[#CCFF00] font-manrope font-semibold uppercase tracking-widest text-sm">
            Investment
          </span>
          <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl mt-4">
            TRAINING PACKAGES
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-xl mx-auto">
            Choose the session that fits your needs. All packages include personalized instruction and video analysis.
          </p>
        </div>

        {/* Single Sessions */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {singlePackages.map((pkg) => {
            const Icon = pkg.icon;
            return (
              <div 
                key={pkg.id}
                className={`bg-[#0A0A0A] p-8 rounded-sm card-hover relative ${pkg.popular ? 'pricing-highlight' : ''}`}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute top-4 right-4 bg-[#CCFF00] text-black text-xs font-bold px-3 py-1 rounded-sm uppercase">
                    Most Popular
                  </div>
                )}
                <Icon className="text-[#CCFF00] mb-4" size={32} />
                <h3 className="font-anton text-2xl mb-2">{pkg.name}</h3>
                <p className="text-[#A1A1AA] text-sm mb-4">{pkg.description}</p>
                <div className="mb-6">
                  <span className="font-anton text-5xl text-[#CCFF00]">${pkg.price}</span>
                  <span className="text-[#A1A1AA] text-sm ml-2">/ {pkg.duration}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                      <Check size={16} className="text-[#CCFF00]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  className={pkg.popular ? 'btn-primary w-full' : 'btn-secondary w-full'}
                  onClick={() => onSelectPackage(pkg.id)}
                  data-testid={`buy-${pkg.id}`}
                >
                  Select Package
                </Button>
              </div>
            );
          })}
        </div>

        {/* Multi-Session Packages */}
        <div className="mt-16">
          <h3 className="font-anton text-2xl text-center mb-8">PACKAGE DEALS</h3>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-3xl mx-auto">
            {multiPackages.map((pkg) => {
              const Icon = pkg.icon;
              return (
                <div 
                  key={pkg.id}
                  className={`bg-[#0A0A0A] p-8 rounded-sm card-hover relative ${pkg.popular ? 'pricing-highlight' : ''}`}
                  data-testid={`pricing-card-${pkg.id}`}
                >
                  {pkg.popular && (
                    <div className="absolute top-4 right-4 bg-[#CCFF00] text-black text-xs font-bold px-3 py-1 rounded-sm uppercase">
                      Best Value
                    </div>
                  )}
                  {pkg.savings && (
                    <div className="absolute top-4 left-4 bg-white/10 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase">
                      Save ${pkg.savings}
                    </div>
                  )}
                  <Icon className="text-[#CCFF00] mb-4 mt-4" size={32} />
                  <h3 className="font-anton text-2xl mb-2">{pkg.name}</h3>
                  <p className="text-[#A1A1AA] text-sm mb-4">{pkg.description}</p>
                  <div className="mb-6">
                    <span className="font-anton text-5xl text-[#CCFF00]">${pkg.price}</span>
                    <span className="text-[#A1A1AA] text-sm ml-2">/ {pkg.duration}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                        <Check size={16} className="text-[#CCFF00]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={pkg.popular ? 'btn-primary w-full' : 'btn-secondary w-full'}
                    onClick={() => onSelectPackage(pkg.id)}
                    data-testid={`buy-${pkg.id}`}
                  >
                    Select Package
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Practice */}
        <div className="mt-16">
          <div className="bg-[#0A0A0A] p-8 md:p-12 rounded-sm card-hover max-w-3xl mx-auto text-center border border-white/10">
            <Users className="text-[#CCFF00] mx-auto mb-4" size={48} />
            <h3 className="font-anton text-3xl mb-4">TEAM PRACTICE</h3>
            <p className="text-[#A1A1AA] mb-6 max-w-md mx-auto">
              Looking to train your whole team? Get custom pricing for groups of 3+ players, travel ball teams, or high school programs.
            </p>
            <a 
              href="https://www.instagram.com/theformulabaseball/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="btn-primary" data-testid="team-contact-btn">
                <Mail size={18} className="mr-2" />
                Contact for Team Pricing
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (e) {
        console.error("Error fetching testimonials:", e);
      }
    };
    fetchTestimonials();
  }, []);

  // Double the testimonials for infinite scroll effect
  const doubledTestimonials = [...testimonials, ...testimonials];

  return (
    <section className="py-24 md:py-32 overflow-hidden section-darker" id="testimonials">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <span className="text-[#CCFF00] font-manrope font-semibold uppercase tracking-widest text-sm">
          Success Stories
        </span>
        <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl mt-4">
          WHAT PLAYERS SAY
        </h2>
      </div>

      {/* Marquee */}
      <div className="relative">
        <div className="flex animate-marquee">
          {doubledTestimonials.map((testimonial, i) => (
            <div key={i} className="testimonial-card" data-testid={`testimonial-${i}`}>
              <p className="text-white font-manrope mb-6 leading-relaxed">"{testimonial.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#CCFF00] flex items-center justify-center text-black font-bold">
                  {testimonial.name?.charAt(0)}
                </div>
                <div>
                  <div className="text-white font-semibold">{testimonial.name}</div>
                  <div className="text-[#A1A1AA] text-sm">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Client Success Section
const ClientSuccessSection = () => {
  const successStories = [
    {
      image: "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/zxxzis0f_PNG%20image.png",
      name: "Caleb Anderson",
      achievement: "Signed with Easton Army",
      category: "Sponsorship"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/uuntlj7l_United%20Kingdom%20-%20Artboard%203.png",
      name: "Eli Orellana",
      achievement: "All-American Silver Slugger Award",
      category: "Award"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_502bc636-2a56-4719-81c4-8a64fd853b75/artifacts/ueh1qfc7_PNG%20image.png",
      name: "Jaylen Carpenter",
      achievement: "2025 PG SoCal Tournament MVP",
      category: "Tournament MVP"
    }
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden" id="success">
      {/* Background Text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <span className="font-anton text-[15vw] text-[#121212] whitespace-nowrap">WHO'S NEXT</span>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <span className="text-[#CCFF00] font-manrope font-semibold uppercase tracking-widest text-sm">
            They Put In The Work
          </span>
          <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl mt-4">
            CLIENT SUCCESS
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-xl mx-auto">
            From local fields to national stages. These players trusted The Formula and the results speak for themselves.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {successStories.map((story, index) => (
            <div 
              key={index}
              className="bg-[#0A0A0A] rounded-sm overflow-hidden card-hover group"
              data-testid={`success-story-${index}`}
            >
              <div className="aspect-square overflow-hidden">
                <img 
                  src={story.image}
                  alt={`${story.name} - ${story.achievement}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <span className="text-[#CCFF00] text-xs font-semibold uppercase tracking-wider">{story.category}</span>
                <h3 className="font-anton text-xl mt-2">{story.name}</h3>
                <p className="text-[#A1A1AA] text-sm mt-1">{story.achievement}</p>
              </div>
            </div>
          ))}
        </div>

        {/* WHO'S NEXT CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block">
            <h3 className="font-anton text-5xl md:text-7xl lg:text-8xl text-white">
              WHO'S <span className="text-[#CCFF00]">NEXT</span>?
            </h3>
            <p className="text-[#A1A1AA] mt-4 text-lg">Your name could be here. Your moment is waiting.</p>
            <a href="#pricing" className="inline-block mt-6">
              <Button className="btn-primary text-lg px-10 py-5" data-testid="whos-next-cta">
                Start Your Journey
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Book Now Section
const BookSection = () => {
  return (
    <section className="py-24 md:py-32 px-6 md:px-12" id="book">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-[#CCFF00] font-manrope font-semibold uppercase tracking-widest text-sm">
            Ready to Level Up?
          </span>
          <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl mt-4">
            BOOK YOUR SESSION
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-xl mx-auto">
            Check available times below, then select and pay for your package above to confirm your booking.
          </p>
        </div>

        {/* How it works */}
        <div className="bg-[#0A0A0A] rounded-sm p-6 md:p-8 mb-8 max-w-3xl mx-auto border border-white/10">
          <h3 className="font-anton text-xl mb-4 text-center">HOW TO BOOK</h3>
          <div className="flex flex-col md:flex-row justify-center gap-6 md:gap-12">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#CCFF00] text-black font-bold flex items-center justify-center flex-shrink-0">1</div>
              <p className="text-[#A1A1AA] text-sm">Check availability below</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#CCFF00] text-black font-bold flex items-center justify-center flex-shrink-0">2</div>
              <p className="text-[#A1A1AA] text-sm"><a href="#pricing" className="text-[#CCFF00] underline">Select & pay</a> for your package</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#CCFF00] text-black font-bold flex items-center justify-center flex-shrink-0">3</div>
              <p className="text-[#A1A1AA] text-sm">Book your time slot</p>
            </div>
          </div>
        </div>

        {/* Calendly Embed - View Only */}
        <div className="bg-[#0A0A0A] rounded-sm p-4 md:p-8 relative">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-[#CCFF00] text-black px-4 py-2 rounded-sm font-bold text-sm uppercase tracking-wider">
            View Availability
          </div>
          <iframe 
            src={CALENDLY_LINK}
            width="100%"
            height="700"
            frameBorder="0"
            title="View available session times"
            className="rounded-sm"
            data-testid="calendly-embed"
          />
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="relative py-16 px-6 md:px-12 border-t border-white/10">
      {/* Background Text */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="footer-bg-text text-center">THE FORMULA</div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          {/* Brand */}
          <div>
            <h3 className="font-anton text-2xl mb-4">THE FORMULA</h3>
            <p className="text-[#A1A1AA] text-sm">
              Elite private hitting instruction in Los Angeles, California.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-manrope font-semibold uppercase tracking-wider text-sm mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><a href="#about" className="text-[#A1A1AA] hover:text-[#CCFF00] transition-colors text-sm">About</a></li>
              <li><a href="#pricing" className="text-[#A1A1AA] hover:text-[#CCFF00] transition-colors text-sm">Pricing</a></li>
              <li><a href="#testimonials" className="text-[#A1A1AA] hover:text-[#CCFF00] transition-colors text-sm">Reviews</a></li>
              <li><a href="#book" className="text-[#A1A1AA] hover:text-[#CCFF00] transition-colors text-sm">Book Now</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-manrope font-semibold uppercase tracking-wider text-sm mb-4">Contact</h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                <MapPin size={16} className="text-[#CCFF00]" />
                Los Angeles, CA
              </li>
              <li>
                <a href="https://www.instagram.com/theformulabaseball/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#CCFF00] transition-colors text-sm">
                  <Instagram size={16} />
                  @theformulabaseball
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#52525B] text-sm">
            © {new Date().getFullYear()} theformulabaseball.com. All rights reserved.
          </p>
          <a href={CALENDLY_LINK} target="_blank" rel="noopener noreferrer">
            <Button className="btn-primary text-sm" data-testid="footer-cta">
              Start Training <ChevronRight size={16} className="ml-1" />
            </Button>
          </a>
        </div>
      </div>
    </footer>
  );
};

// Payment Modal
const PaymentModal = ({ isOpen, onClose, packageId, packages }) => {
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);
  const [checkingDiscount, setCheckingDiscount] = useState(false);

  const selectedPackage = packages[packageId];
  const finalPrice = discountApplied ? discountApplied.discount_price : selectedPackage?.price;

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    
    setCheckingDiscount(true);
    try {
      const response = await axios.post(`${API}/validate-discount?package_id=${packageId}&code=${discountCode}`);
      if (response.data.valid) {
        setDiscountApplied(response.data);
        toast.success(`Discount applied! ${response.data.description}`);
      } else {
        toast.error("Invalid discount code for this package");
        setDiscountApplied(null);
      }
    } catch (error) {
      toast.error("Failed to validate discount code");
    } finally {
      setCheckingDiscount(false);
    }
  };

  const handlePayment = async () => {
    if (!customerName || !customerEmail) {
      toast.error("Please fill in your name and email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        package_id: packageId,
        origin_url: window.location.origin,
        customer_name: customerName,
        customer_email: customerEmail,
        discount_code: discountApplied ? discountCode : null
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Reset discount when modal closes or package changes
  useEffect(() => {
    setDiscountApplied(null);
    setDiscountCode("");
  }, [packageId, isOpen]);

  if (!selectedPackage) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-anton text-2xl">{selectedPackage.name}</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Complete your booking by entering your details below.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="bg-[#121212] p-4 rounded-sm">
            <div className="flex justify-between items-center">
              <span className="text-[#A1A1AA]">{selectedPackage.duration}</span>
              <div className="text-right">
                {discountApplied ? (
                  <>
                    <span className="text-[#A1A1AA] line-through text-lg mr-2">${selectedPackage.price}</span>
                    <span className="font-anton text-3xl text-[#CCFF00]">${finalPrice}</span>
                  </>
                ) : (
                  <span className="font-anton text-3xl text-[#CCFF00]">${selectedPackage.price}</span>
                )}
              </div>
            </div>
            <p className="text-sm text-[#A1A1AA] mt-2">{selectedPackage.description}</p>
            {discountApplied && (
              <p className="text-sm text-[#CCFF00] mt-2">✓ {discountApplied.description}</p>
            )}
          </div>

          <div>
            <label className="text-sm text-[#A1A1AA] block mb-2">Your Name</label>
            <Input 
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="John Doe"
              className="bg-[#121212] border-white/10 text-white"
              data-testid="payment-name-input"
            />
          </div>

          <div>
            <label className="text-sm text-[#A1A1AA] block mb-2">Email Address</label>
            <Input 
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="john@example.com"
              className="bg-[#121212] border-white/10 text-white"
              data-testid="payment-email-input"
            />
          </div>

          {/* Discount Code */}
          <div>
            <label className="text-sm text-[#A1A1AA] block mb-2">Discount Code (optional)</label>
            <div className="flex gap-2">
              <Input 
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="bg-[#121212] border-white/10 text-white uppercase"
                data-testid="discount-code-input"
                disabled={discountApplied}
              />
              {discountApplied ? (
                <Button 
                  variant="outline"
                  className="border-white/10 text-[#A1A1AA] hover:text-white"
                  onClick={() => {
                    setDiscountApplied(null);
                    setDiscountCode("");
                  }}
                  data-testid="remove-discount-btn"
                >
                  Remove
                </Button>
              ) : (
                <Button 
                  variant="outline"
                  className="border-white/10 text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
                  onClick={applyDiscount}
                  disabled={checkingDiscount || !discountCode.trim()}
                  data-testid="apply-discount-btn"
                >
                  {checkingDiscount ? "..." : "Apply"}
                </Button>
              )}
            </div>
          </div>

          <Button 
            className="btn-primary w-full mt-4"
            onClick={handlePayment}
            disabled={loading}
            data-testid="confirm-payment-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="spinner" /> Processing...
              </span>
            ) : (
              `Pay $${finalPrice} Now`
            )}
          </Button>

          <p className="text-xs text-[#52525B] text-center">
            Secure payment powered by Stripe
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Payment Status Handler
const PaymentStatusHandler = ({ type = "lesson" }) => {
  const [searchParams] = useSearchParams();
  const [checkingStatus, setCheckingStatus] = useState(false);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info("Payment status check completed. Check your email for confirmation.");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        if (type === "merch") {
          toast.success("Payment successful! Your order is confirmed. We'll ship it soon!");
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Lesson payment - redirect to Calendly to book
          toast.success("Payment successful! Now let's book your session...");
          setTimeout(() => {
            window.location.href = CALENDLY_LINK;
          }, 2000);
        }
        return;
      } else if (response.data.status === 'expired') {
        toast.error("Payment session expired. Please try again.");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  }, [type]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success' && !checkingStatus) {
      setCheckingStatus(true);
      toast.info("Verifying your payment...");
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info("Payment was cancelled. Feel free to try again when you're ready.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams, checkingStatus, pollPaymentStatus]);

  return null;
};

// Shop Page Component
const ShopPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [products, setProducts] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/merch`);
        setProducts(response.data);
      } catch (e) {
        console.error("Error fetching products:", e);
      }
    };
    fetchProducts();
  }, []);

  const productList = Object.entries(products).map(([id, product]) => ({
    id,
    ...product
  }));

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="shop-page">
      <Toaster position="top-center" richColors theme="dark" />
      <PaymentStatusHandler type="merch" />
      <Navigation scrolled={scrolled} />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#CCFF00] transition-colors mb-8">
            <ArrowLeft size={18} />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <ShoppingBag className="text-[#CCFF00]" size={32} />
            <span className="text-[#CCFF00] font-manrope font-semibold uppercase tracking-widest text-sm">
              Official Merch
            </span>
          </div>
          <h1 className="font-anton text-4xl sm:text-5xl lg:text-6xl">
            THE FORMULA <span className="text-[#CCFF00]">COLLECTION</span>
          </h1>
          <p className="text-[#A1A1AA] mt-4 max-w-xl">
            Rep The Formula with our premium vintage wash tees. Limited drops, unlimited swag.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productList.map((product) => (
              <div 
                key={product.id}
                className="bg-[#0A0A0A] rounded-sm overflow-hidden card-hover group"
                data-testid={`product-${product.id}`}
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                {/* Product Image */}
                <div className="aspect-square overflow-hidden bg-[#121212] relative">
                  <img 
                    src={hoveredProduct === product.id && product.image_back !== product.image_front ? product.image_back : product.image_front}
                    alt={product.name}
                    className="w-full h-full object-contain transition-all duration-500"
                  />
                  {product.image_back !== product.image_front && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-[#A1A1AA] bg-black/50 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      Hover for back
                    </div>
                  )}
                </div>
                
                {/* Product Info */}
                <div className="p-6">
                  <h3 className="font-anton text-xl mb-2">{product.name}</h3>
                  <p className="text-[#A1A1AA] text-sm mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-anton text-3xl text-[#CCFF00]">${product.price}</span>
                    <Button 
                      className="btn-primary"
                      onClick={() => {
                        setSelectedProduct(product);
                        setModalOpen(true);
                      }}
                      data-testid={`buy-${product.id}`}
                    >
                      Buy Now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Size Guide */}
      <section className="py-16 px-6 md:px-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#A1A1AA]">
            All tees are vintage wash, heavyweight cotton with a relaxed fit. 
            <span className="text-white ml-1">Size up for an oversized look.</span>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#52525B] text-sm">
            © {new Date().getFullYear()} theformulabaseball.com. All rights reserved.
          </p>
          <Link to="/">
            <Button className="btn-secondary text-sm">
              Back to Training
            </Button>
          </Link>
        </div>
      </footer>

      {/* Purchase Modal */}
      <MerchModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        product={selectedProduct}
      />
    </div>
  );
};

// Merch Purchase Modal
const MerchModal = ({ isOpen, onClose, product }) => {
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [size, setSize] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingZip, setShippingZip] = useState("");

  const handlePurchase = async () => {
    if (!customerName || !customerEmail || !size || !shippingAddress || !shippingCity || !shippingState || !shippingZip) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/merch/checkout`, {
        product_id: product.id,
        size: size,
        origin_url: window.location.origin,
        customer_name: customerName,
        customer_email: customerEmail,
        shipping_address: shippingAddress,
        shipping_city: shippingCity,
        shipping_state: shippingState,
        shipping_zip: shippingZip
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-anton text-2xl">{product.name}</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Complete your order details below.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Product Preview */}
          <div className="bg-[#121212] rounded-sm overflow-hidden">
            <img 
              src={product.image_front}
              alt={product.name}
              className="w-full h-40 object-contain"
            />
          </div>

          <div className="flex justify-between items-center py-2">
            <span className="text-[#A1A1AA]">{product.description}</span>
            <span className="font-anton text-2xl text-[#CCFF00]">${product.price}</span>
          </div>

          {/* Size Selection */}
          <div>
            <label className="text-sm text-[#A1A1AA] block mb-2">Select Size *</label>
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger className="bg-[#121212] border-white/10 text-white" data-testid="size-select">
                <SelectValue placeholder="Choose your size" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="S">Small</SelectItem>
                <SelectItem value="M">Medium</SelectItem>
                <SelectItem value="L">Large</SelectItem>
                <SelectItem value="XL">X-Large</SelectItem>
                <SelectItem value="XXL">XX-Large</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Name *</label>
              <Input 
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="John Doe"
                className="bg-[#121212] border-white/10 text-white"
                data-testid="merch-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Email *</label>
              <Input 
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="john@example.com"
                className="bg-[#121212] border-white/10 text-white"
                data-testid="merch-email-input"
              />
            </div>
          </div>

          {/* Shipping Address */}
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-sm font-semibold text-[#CCFF00] mb-3 uppercase tracking-wider">Shipping Address</h4>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Street Address *</label>
                <Input 
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="123 Main St, Apt 4"
                  className="bg-[#121212] border-white/10 text-white"
                  data-testid="shipping-address-input"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-[#A1A1AA] block mb-2">City *</label>
                  <Input 
                    value={shippingCity}
                    onChange={(e) => setShippingCity(e.target.value)}
                    placeholder="Los Angeles"
                    className="bg-[#121212] border-white/10 text-white"
                    data-testid="shipping-city-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#A1A1AA] block mb-2">State *</label>
                  <Input 
                    value={shippingState}
                    onChange={(e) => setShippingState(e.target.value)}
                    placeholder="CA"
                    className="bg-[#121212] border-white/10 text-white"
                    data-testid="shipping-state-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#A1A1AA] block mb-2">ZIP *</label>
                  <Input 
                    value={shippingZip}
                    onChange={(e) => setShippingZip(e.target.value)}
                    placeholder="90001"
                    className="bg-[#121212] border-white/10 text-white"
                    data-testid="shipping-zip-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <Button 
            className="btn-primary w-full mt-4"
            onClick={handlePurchase}
            disabled={loading}
            data-testid="confirm-merch-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="spinner" /> Processing...
              </span>
            ) : (
              `Pay $${product.price} Now`
            )}
          </Button>

          <p className="text-xs text-[#52525B] text-center">
            Secure payment powered by Stripe
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Home Component
const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/packages`);
        setPackages(response.data);
      } catch (e) {
        console.error("Error fetching packages:", e);
      }
    };
    fetchPackages();
  }, []);

  const handleSelectPackage = (packageId) => {
    setSelectedPackage(packageId);
    setPaymentModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <Toaster position="top-center" richColors theme="dark" />
      <PaymentStatusHandler />
      <Navigation scrolled={scrolled} />
      <HeroSection />
      <AboutSection />
      <PricingSection onSelectPackage={handleSelectPackage} />
      <ClientSuccessSection />
      <TestimonialsSection />
      <BookSection />
      <Footer />
      <PaymentModal 
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        packageId={selectedPackage}
        packages={packages}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<ShopPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
